import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart, Server, Ticket, Shield, Package, Users, Key, DollarSign,
  RefreshCw, Activity, Loader2, ArrowLeft, AlertTriangle, CheckCircle,
  Clock, XCircle, HelpCircle, ExternalLink,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';

function StatCard({ label, value, color, icon: Icon, onClick, testId }) {
  return (
    <Card className={`${onClick ? 'cursor-pointer hover:shadow-md' : ''} ${color ? `border-${color}-200` : ''}`}
      onClick={onClick} data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-gray-500 uppercase">{label}</p>
            <p className={`text-xl font-bold ${color ? `text-${color}-600` : ''}`}>{value}</p>
          </div>
          {Icon && <Icon className={`h-5 w-5 ${color ? `text-${color}-400` : 'text-gray-300'}`} />}
        </div>
      </CardContent>
    </Card>
  );
}

export default function NinjaReports() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('fleet');
  const [loading, setLoading] = useState(true);
  const [fleet, setFleet] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [licenses, setLicenses] = useState(null);
  const [sync, setSync] = useState(null);
  const [actions, setActions] = useState(null);
  const [capability, setCapability] = useState(null);

  const loadReport = useCallback(async (reportTab) => {
    setLoading(true);
    try {
      if (reportTab === 'fleet') {
        const r = await api.get('/ninja-reports/fleet-health');
        setFleet(r.data);
      } else if (reportTab === 'tickets') {
        const r = await api.get('/ninja-reports/tickets');
        setTickets(r.data);
      } else if (reportTab === 'licenses') {
        const r = await api.get('/ninja-reports/licenses');
        setLicenses(r.data);
      } else if (reportTab === 'sync') {
        const r = await api.get('/ninja-reports/sync-integration');
        setSync(r.data);
      } else if (reportTab === 'actions') {
        const r = await api.get('/ninja-reports/rmm-actions');
        setActions(r.data);
      } else if (reportTab === 'capability') {
        const r = await api.get('/ninja-reports/capability-matrix');
        setCapability(r.data);
      }
    } catch (e) {
      toast.error(`Failed to load report: ${e.response?.data?.detail || e.message}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadReport(tab); }, [tab, loadReport]);

  const freshness = (data) => {
    if (!data?.generated_at) return null;
    return (
      <span className="text-[10px] text-gray-400 flex items-center gap-1">
        <Clock className="h-3 w-3" />
        {data.data_source === 'persisted' ? 'Persisted data' : 'Live'} · Generated {formatInCST(data.generated_at, 'h:mm:ss a')}
      </span>
    );
  };

  return (
    <div className="space-y-6 p-6" data-testid="ninja-reports-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ninjarmm')}><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart className="h-6 w-6 text-blue-600" /> NinjaOne Reports</h1>
            <p className="text-sm text-gray-500">Operational reporting from persisted CipherOne data</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => loadReport(tab)} data-testid="refresh-report">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="report-tabs">
          <TabsTrigger value="fleet">Fleet Health</TabsTrigger>
          <TabsTrigger value="tickets">Tickets</TabsTrigger>
          <TabsTrigger value="licenses">Licenses</TabsTrigger>
          <TabsTrigger value="sync">Sync/Integration</TabsTrigger>
          <TabsTrigger value="actions">RMM Actions</TabsTrigger>
          <TabsTrigger value="capability">Capability</TabsTrigger>
        </TabsList>

        {loading && (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        )}

        {!loading && (
          <>
            <TabsContent value="fleet">
              {fleet && (
                <div className="space-y-4">
                  {freshness(fleet)}
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                    <StatCard label="Total Devices" value={fleet.total} icon={Server} onClick={() => navigate('/assets?has_ninjarmm=true')} testId="rpt-total" />
                    <StatCard label="Online" value={fleet.online} color="emerald" icon={CheckCircle} />
                    <StatCard label="Offline" value={fleet.offline} color="red" icon={XCircle} onClick={() => navigate('/assets?status=inactive')} testId="rpt-offline" />
                    <StatCard label="Needs Attention" value={fleet.needs_attention} color="amber" icon={AlertTriangle} testId="rpt-attention" />
                    <StatCard label="Active Alerts" value={fleet.active_alerts} color="red" icon={AlertTriangle} />
                    <StatCard label="Stale Inventory" value={fleet.stale_inventory} color="amber" icon={Clock} />
                  </div>
                  {fleet.offline_devices?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">Devices Needing Attention</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-1">
                          {fleet.offline_devices.map(d => (
                            <button key={d.id} onClick={() => navigate(`/assets/${d.id}`)}
                              className="flex items-center justify-between w-full text-left p-2 hover:bg-gray-50 rounded text-sm">
                              <span className="font-medium">{d.name}</span>
                              <span className="text-gray-500 text-xs">{d.customer_name}</span>
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="tickets">
              {tickets && (
                <div className="space-y-4">
                  {freshness(tickets)}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatCard label="Open" value={tickets.total_open} color="blue" icon={Ticket} onClick={() => navigate('/tickets')} testId="rpt-open" />
                    <StatCard label="Unassigned" value={tickets.unassigned} color="amber" icon={HelpCircle} />
                    <StatCard label="Critical" value={tickets.critical} color="red" icon={AlertTriangle} />
                    <StatCard label="SLA Breached" value={tickets.sla_breached} color="red" icon={XCircle} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">By Source</CardTitle></CardHeader>
                      <CardContent className="space-y-1">
                        {Object.entries(tickets.source_counts || {}).map(([source, count]) => (
                          <div key={source} className="flex justify-between text-sm">
                            <span className="capitalize text-gray-600">{source || 'manual'}</span>
                            <span className="font-medium">{count}</span>
                          </div>
                        ))}
                        {Object.keys(tickets.source_counts || {}).length === 0 && <p className="text-gray-400 text-sm">No ticket data</p>}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2"><CardTitle className="text-sm">By Status</CardTitle></CardHeader>
                      <CardContent className="space-y-1">
                        {Object.entries(tickets.status_counts || {}).map(([status, count]) => (
                          <div key={status} className="flex justify-between text-sm">
                            <span className="capitalize text-gray-600">{status?.replace('_', ' ')}</span>
                            <span className="font-medium">{count}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="licenses">
              {licenses && (
                <div className="space-y-4">
                  {freshness(licenses)}
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                    <StatCard label="Total Licenses" value={licenses.total_licenses} icon={Key} />
                    <StatCard label="Purchased" value={licenses.total_purchased} />
                    <StatCard label="Assigned" value={licenses.total_assigned} />
                    <StatCard label="Available" value={licenses.total_available} color="emerald" />
                    <StatCard label="Expired" value={licenses.expired} color="red" />
                    <StatCard label="Expiring Soon" value={licenses.expiring_soon} color="amber" />
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="sync">
              {sync && (
                <div className="space-y-4">
                  {freshness(sync)}
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Overall Health: {sync.overall_health}</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      {Object.entries(sync.subsystems || {}).map(([key, sub]) => (
                        <div key={key} className="flex items-center justify-between p-2 rounded border">
                          <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                          <Badge variant="outline" className={`text-[10px] ${
                            sub.status === 'HEALTHY' ? 'border-emerald-300 text-emerald-700' :
                            sub.status === 'DEGRADED' ? 'border-amber-300 text-amber-700' :
                            sub.status === 'DISABLED' ? 'border-gray-300 text-gray-400' :
                            'border-gray-300'
                          }`}>{sub.status}</Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            <TabsContent value="actions">
              {actions && (
                <div className="space-y-4">
                  {freshness(actions)}
                  <div className="grid grid-cols-2 gap-3">
                    <StatCard label="Total Actions" value={actions.total_actions} icon={Activity} />
                    <StatCard label="Failed" value={actions.failed_actions} color="red" icon={XCircle} />
                  </div>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">By Action Type</CardTitle></CardHeader>
                    <CardContent className="space-y-1">
                      {Object.entries(actions.action_type_counts || {}).map(([type, count]) => (
                        <div key={type} className="flex justify-between text-sm">
                          <span className="text-gray-600">{type}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                      {Object.keys(actions.action_type_counts || {}).length === 0 && <p className="text-gray-400 text-sm">No RMM actions recorded</p>}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            <TabsContent value="capability">
              {capability && (
                <div className="space-y-4">
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">NinjaOne Capability Matrix</CardTitle></CardHeader>
                    <CardContent className="space-y-1">
                      {Object.entries(capability.capabilities || {}).map(([key, cap]) => (
                        <div key={key} className="flex items-center justify-between p-1.5 rounded hover:bg-gray-50 text-sm">
                          <span className="text-gray-700 capitalize">{key.replace(/_/g, ' ')}</span>
                          <div className="flex items-center gap-2">
                            {cap.access && <span className="text-[10px] text-gray-400">{cap.access}</span>}
                            <Badge variant="outline" className={`text-[10px] ${
                              cap.status === 'IMPLEMENTED' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
                              cap.status === 'PARTIAL' ? 'border-amber-300 text-amber-700 bg-amber-50' :
                              'border-gray-300 text-gray-500'
                            }`}>{cap.status}</Badge>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Remote Access Assessment (N7 Input)</CardTitle></CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div>
                        <p className="font-medium">Ninja Remote</p>
                        <p className="text-gray-500">{capability.remote_access_assessment?.ninja_remote?.supported_approach}</p>
                        <p className="text-xs text-gray-400">{capability.remote_access_assessment?.ninja_remote?.limitation}</p>
                      </div>
                      <div>
                        <p className="font-medium">Quick Connect</p>
                        <p className="text-gray-500">{capability.remote_access_assessment?.quick_connect?.supported_approach}</p>
                        <p className="text-xs text-gray-400">{capability.remote_access_assessment?.quick_connect?.recommendation}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}
